import { z } from 'zod';
/**
 * Wire types for the Node backend ↔ Python Kennisagent service.
 *
 * This file is the SOURCE OF TRUTH. Python Pydantic models are generated from
 * this via JSON Schema + datamodel-code-generator (see agents/kennisagent/Makefile).
 * To change the wire shape: edit this file, then run `make schemas` in
 * agents/kennisagent/, commit both files. CI fails if `git diff` is non-empty
 * after regen.
 *
 * Slice 3a wire shape: POST /v1/agent/ask
 */
export const AgentTenantId = z.string().min(1).max(100);
export const AgentUserId = z.string().min(1).max(100);
export const AgentAskRequest = z.object({
    /**
     * Caller-provided idempotency / tracing handle. Backend mints a UUID per
     * `/api/v1/agent/ask` call and forwards it.
     */
    requestId: z.string().min(1).max(100),
    tenantId: AgentTenantId,
    /**
     * For BigQuery telemetry actor_user_id field. NEVER used to filter retrieval —
     * Ring 1 corpus is shared. Slice 4 will add tenant-private Ring 2 lookups.
     */
    userId: AgentUserId.optional(),
    actorRole: z.string().max(50).optional(),
    /**
     * The Dutch clinical question. Capped at 4000 chars to bound prompt cost.
     * Raised from 1000 on 2026-07-03: the Sara→Mila hand-off pre-fills a
     * question (consult-samenvatting + full SOEP + intent) that routinely
     * exceeds 1000, and standalone casus descriptions (mode='plan') don't fit
     * in 1000 either.
     */
    query: z.string().min(1).max(4000),
    /**
     * Optional explicit corpus scope from the frontend's scope picker. When
     * present, retrieval hard-filters to chunks whose `documentTitle` is in
     * this list. Capped at 30 (Firestore `in`-query limit). Absent or empty
     * means "scan the entire Ring 1 corpus" (default).
     */
    documentTitles: z.array(z.string().min(1).max(500)).max(30).optional(),
    /**
     * Last N user/assistant messages in the same chat (oldest-first), capped
     * to 10 (= 5 Q&A pairs) to bound prompt cost. Lets the answer-side agent
     * naturally reference earlier context ("aanvullend op de eerder besproken
     * SSRI-aanpak…") without re-citing prior sources unnecessarily.
     */
    priorMessages: z
        .array(z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().min(1).max(10000),
    }))
        .max(10)
        .optional(),
    /**
     * Sticky head — the first user question in the chat, truncated. Always
     * rides along even after the original message itself drops out of the
     * `priorMessages` sliding window. Lets the agent stay grounded in the
     * conversation's original framing ("we started by asking about
     * depression treatment in adults") even at turn 12. Backend only sends
     * this when the thread is long enough that turn 1 has fallen out of
     * the sliding window — otherwise the first message is already in
     * `priorMessages` and we'd be duplicating tokens.
     */
    firstQuestionSummary: z.string().min(1).max(300).optional(),
    /**
     * 1-line summaries of what was cited in each prior assistant turn, deduped
     * across the thread, capped at 8 entries. Tells the agent what's already
     * been grounded so it doesn't waste a turn re-citing the same chunks.
     * Format: `"{documentTitle} ({source})"` where source is "SUPPOHRT" for
     * Ring 1 or the Ring 2 source label.
     */
    priorCitationSummaries: z.array(z.string().min(1).max(500)).max(8).optional(),
    /**
     * Ring 0 access flag. Backend sets this to `true` ONLY when the caller's
     * Entra group claims include SUPPOHRT_USERS or SUPPOHRT_ADMINS — that's
     * the load-bearing access control for internal-only docs (personnel
     * handbook, timechimp guides). Agent stays role-blind: it just sees the
     * flag and queries the kennisbank_ring0 collection if true. Forgotten
     * flag = no Ring 0 results (fail-safe), not a leak.
     */
    ring0Enabled: z.boolean().optional(),
    /**
     * Ring HQ (Kantoorpersoneel head-office) access flag. Narrower than
     * `ring0Enabled` — backend sets true ONLY when the caller is in the
     * Kantoorpersoneel Entra group (persisted as `isHeadOffice` on the
     * User record). Gates the `kennisbank_ring_hq` corpus (internal
     * process + policy docs). Same fail-safe shape as ring0Enabled:
     * forgotten flag = no Ring HQ results, never a leak.
     */
    ringHqEnabled: z.boolean().optional(),
    /**
     * Action mode chosen by the practitioner via the input buttons
     * (Verdiepen / Werkvormen / Verwijzen / Plan / Vervolgvraag). Makes the
     * intent explicit instead of letting the model guess it from phrasing.
     * Applies to THIS question only — no sticky state. Absent or 'free' =
     * current behaviour (all rings, tool auto-available).
     *   'verwijzen'  = force a Sociale Kaart provider lookup (find_zorgaanbieders).
     *   'verdiepen'  = prioritise clinical background / theory; no provider tool.
     *   'werkvormen' = prioritise concrete werkvormen / oefeningen / werkbladen.
     *   'plan'       = draft a structured treatment-plan proposal for a casus
     *                  (goals, per-session interventions, evaluation, referral
     *                  criteria); no provider tool.
     */
    mode: z.enum(['free', 'verdiepen', 'werkvormen', 'verwijzen', 'plan']).optional(),
});
/**
 * One row in the scope-picker document list. The frontend renders these
 * as multi-select options. `sourceFile` is the bucket key + canonical
 * identifier; `documentTitle` is what the operator sees.
 */
export const AgentDocumentSummary = z.object({
    sourceFile: z.string().min(1).max(500),
    documentTitle: z.string().min(1).max(500),
    author: z.string().min(1).max(200),
});
export const AgentDocumentsResponse = z.object({
    documents: z.array(AgentDocumentSummary),
});
/**
 * One source card rendered under the answer in the chat UI.
 */
export const AgentCitation = z.object({
    /**
     * Stable chunk ID (sha256 of sourceFile + chunkIndex + chunkText). Frontend
     * never displays this; it's used for click-through (slice 3b) and dedup.
     */
    chunkId: z.string().min(1).max(200),
    /**
     * Display title — manifest-supplied document title; falls back to PDF
     * basename minus extension when the manifest entry is missing.
     */
    title: z.string().min(1).max(500),
    /**
     * Original PDF basename (with extension). Used by the source-download
     * endpoint and for cross-referencing chunks back to the file in GCS.
     */
    sourceFile: z.string().min(1).max(500).optional(),
    /**
     * Author or institution responsible for the document (manifest-supplied).
     * Empty when unknown — the card hides the field rather than showing 'Onbekend'.
     */
    author: z.string().max(200).optional(),
    /**
     * 1-based page number for the chunk's first character.
     */
    page: z.number().int().min(1),
    /**
     * ≤300-char excerpt rendered on the card. Source-language (Dutch).
     */
    snippet: z.string().min(1).max(500),
    /**
     * Trust tier — drives the chip colour on the citation card.
     *   'ring0' = SUPPOHRT-internal corpus (personeelshandboek, way-we-work,
     *             timechimp guides). Visible only to internal SUPPOHRT staff
     *             (Entra group SUPPOHRT_USERS or SUPPOHRT_ADMINS); never to
     *             external practitioners. Distinct chip ("SUPPOHRT Intern").
     *   'ring1' = SUPPOHRT-curated clinical corpus (Mosterdgeel chip).
     *   'ring2' = verified external sources crawled weekly (Petrol chip,
     *             source-name label). Institutional sources: NHG, Trimbos,
     *             Thuisarts.nl, GGZ-standaarden, RIVM.
     *   'ring3' = community-curated practitioner worksheets crawled weekly
     *             (Petrol chip, "Werkbladen" label). Single source today:
     *             Meesters in de Psychologie. Ranked below Ring 2 in the
     *             retrieval merge (R1 > R2 > R3).
     *   'ring_hq' = head-office-only corpus (internal process + policy
     *             docs). Gated by the persisted `isHeadOffice` flag on
     *             the User record (Kantoorpersoneel Entra group). Same
     *             trust tier as Ring 1 in the score boost; rendered with
     *             a distinct "SUPPOHRT Kantoor" chip so head-office
     *             practitioners can tell which docs are HQ-restricted.
     * Defaults to 'ring1' when missing so legacy citations on existing
     * chats don't break.
     */
    ring: z.enum(['ring0', 'ring1', 'ring2', 'ring3', 'ring_hq']).default('ring1'),
    /**
     * For ring='ring2', the canonical source identifier matching
     * shared/src/ring2-sources.ts. Drives the chip label ("NHG", "Trimbos"
     * etc.) and the "Open op {source}.nl" link target. Absent for ring1.
     */
    ring2Source: z.string().min(1).max(40).optional(),
    /**
     * For ring='ring3', the canonical source identifier. Single value
     * today ('meesters'); reserved for when Behandelhulp.nl or another
     * worksheet source is added.
     */
    ring3Source: z.string().min(1).max(40).optional(),
    /**
     * For ring='ring2' or ring='ring3', the original URL of the article
     * on the external source. The "Open op …" button opens this in a new
     * tab. Ring 1 chunks already have sourceFile pointing to the GCS
     * object; Ring 2/3 have no GCS file by design (link-out, never host).
     */
    sourceUrl: z.string().url().max(2000).optional(),
});
/**
 * Slice 8 — Sociale Kaart provider citation. Returned alongside chunk
 * citations when the model invoked the find_zorgaanbieders tool and cited
 * at least one provider via [providerId: <id>] in the answer. Frontend
 * renders these as compact provider cards in the source rail with a click-
 * through to the Sociale Kaart entry.
 *
 * Wire-shape mirrors the relevant subset of the practitioner-facing
 * Provider schema — kept independent of the full record so changes to
 * Sociale Kaart's internal fields don't break the agent contract.
 */
export const AgentProviderCitation = z.object({
    /** Firestore provider doc ID — load-bearing for the click-through link. */
    providerId: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    city: z.string().max(100).nullable().optional(),
    province: z.string().max(50).nullable().optional(),
    /** ProviderType — see shared/src/sociale-kaart.ts. */
    type: z.string().max(50).nullable().optional(),
    specialisations: z.array(z.string().max(100)).max(20).default([]),
    audiences: z.array(z.string().max(50)).max(10).default([]),
    website: z.string().max(500).nullable().optional(),
    phone: z.string().max(50).nullable().optional(),
});
export const AgentAskOutcome = z.enum([
    'success',
    'low_confidence_rewrite',
    'empty_retrieval',
    'refused',
    // Slice 4 §1.3: crisis pre-filter tripped — entirely separate
    // response path. Frontend renders the answer with a distinct visual
    // treatment (red/amber border, hotline buttons), no citation list.
    'crisis',
    // Issue #22: fuzzy match op een niet-exact gespelde bron-/organisatienaam
    // was ambigu of onzeker; Mila stelt een bevestigingsvraag i.p.v. te
    // antwoorden. Rendert als een gewoon antwoordbericht (geen aparte UI) —
    // het multiturn-mechanisme laat de bevestiging in dezelfde chat doorlopen.
    'bron_confirmation',
]);
export const AgentAskResponse = z.object({
    requestId: z.string().min(1).max(100),
    /**
     * Dutch answer text. May be a refusal string when outcome != 'success'.
     * Frontend renders verbatim — no Markdown processing yet (slice 3a).
     */
    answer: z.string().min(1).max(8000),
    citations: z.array(AgentCitation).max(10),
    outcome: AgentAskOutcome,
    /**
     * Multi-turn debug surface. Present when the agent reformulated the
     * (often terse) follow-up into a standalone Dutch query for retrieval.
     * Backend persists this on the assistant `ChatMessage` so a future debug
     * affordance can show "your question was interpreted as: …". Absent on
     * cold-start asks where no reformulation happened.
     */
    reformulatedQuery: z.string().min(1).max(1000).optional(),
    /**
     * Slice 8 — Sociale Kaart providers cited by the model. Empty on
     * answers that didn't invoke the find_zorgaanbieders tool. Cap at 8
     * matches what the tool itself returns.
     */
    providerCitations: z.array(AgentProviderCitation).max(8).default([]),
    /**
     * Diagnostics for backend audit/telemetry (NOT shown to user). Token counts
     * come from Vertex SDK's GenerateContentResponse.usage_metadata.
     */
    diagnostics: z.object({
        promptTokens: z.number().int().nonnegative().optional(),
        completionTokens: z.number().int().nonnegative().optional(),
        durationMs: z.number().int().nonnegative(),
        citationsDropped: z.number().int().nonnegative().default(0),
        retrievedChunkCount: z.number().int().nonnegative(),
        modelId: z.string().min(1).max(100),
        /** Number of provider matches the find_zorgaanbieders tool returned. */
        toolCallCount: z.number().int().nonnegative().optional(),
    }),
});
/**
 * Error envelope. Python service returns these with appropriate HTTP status.
 * Backend maps to user-facing Dutch copy per the plan's error UX section.
 */
export const AgentErrorCode = z.enum([
    'invalid_request',
    'unauthorized',
    'rate_limited',
    'timeout',
    'upstream_error',
    'internal_error',
]);
export const AgentErrorResponse = z.object({
    requestId: z.string().min(1).max(100).optional(),
    error: AgentErrorCode,
    message: z.string().min(1).max(1000),
});
/**
 * Display-time translation of an already-grounded Dutch answer to
 * English. Linda feedback 2026-05-20. No retrieval, no citation
 * regeneration — citation markers are preserved verbatim in the
 * translated text.
 */
export const AgentTranslateRequest = z.object({
    tenantId: AgentTenantId,
    userId: AgentUserId,
    chatId: z.string().min(1).max(100),
    messageId: z.string().min(1).max(100),
    sourceText: z.string().min(1).max(16000),
    targetLanguage: z.literal('en'),
});
export const AgentTranslateResponse = z.object({
    text: z.string(),
    targetLanguage: z.literal('en'),
    modelId: z.string(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
});
/**
 * Sara SOEP-generation (issue #21, ADR 0006). A raw consult transcript
 * goes in; a de-identified SOEP verslag + a substantive de-identified
 * clinical summary come out. De-identification happens in the agent
 * (Gemini + a regex output-backstop for BSN/e-mail/phone/postcode) so no
 * raw clinical text is persisted or logged. The transcript is transient:
 * it lives only in browser state and this one Vertex call.
 */
export const SoepFields = z.object({
    S: z.string(),
    O: z.string(),
    E: z.string(),
    P: z.string(),
});
export const AgentSoepRequest = z.object({
    tenantId: AgentTenantId,
    userId: AgentUserId,
    transcript: z.string().min(1).max(16000),
});
export const AgentSoepResponse = z.object({
    soep: SoepFields,
    summary: z.string(),
    modelId: z.string(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
});
//# sourceMappingURL=agent.js.map